import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Button, Fade, Accordion, AccordionSummary, AccordionDetails, Box } from '@mui/material';
import { Add as AddIcon, Description as TemplateIcon, ExpandMore as ExpandMoreIcon, Category as CategoryIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Navbar from '../../shared/Navbar';
import Breadcrumb from '../../shared/Breadcrumb';
import TemplateRow from '../components/TemplateCard';
import CreateTemplateForm from '../components/CreateTemplateCard';
import TemplateModal from '../components/TemplateModal';
import CommonDeleteConfirmDialog from '../../shared/CommonDeleteConfirmDialog';
import PromptTypeModal from '../components/PromptTypeModal';
import {
  addTemplate,
  updateTemplate,
  deleteTemplate,
  setActiveTemplate
} from '../../../store/promptTemplateSlice';
import { COLORS } from '../../../shared/colors';
import { MOCK_PROMPT_TEMPLATES } from '../../../shared/mockData';
import './PromptTemplates.css';

const PromptTemplates = () => {
  const dispatch = useDispatch();
  const { templates = MOCK_PROMPT_TEMPLATES, promptTypes } = useSelector(state => state.promptTemplates);
  
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  
  // Group templates by type
  const groupedTemplates = promptTypes.reduce((groups, type) => {
    groups[type] = templates.filter(template => template.type === type);
    return groups;
  }, {});
  
  const [isCreating, setIsCreating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [editingTemplate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const handleCreateNew = (templateData) => {
    dispatch(addTemplate(templateData));
    setIsCreating(false);
  };

  const handleShowCreateForm = () => {
    setIsCreating(true);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
  };

  const handleEdit = (templateData) => {
    dispatch(updateTemplate(templateData));
    toast.success('Template updated successfully!');
  };

  const handleSave = (templateData) => {
    dispatch(updateTemplate(templateData));
  };

  const handleDeleteClick = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      dispatch(deleteTemplate(templateToDelete.id));
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleSetActive = (templateId, type, isActive) => {
    dispatch(setActiveTemplate({ templateId, type, isActive }));
  };

  return (
    <div className="dashboard">
      <Navbar />
      
      <main className="main prompt-templates">
        <Fade in timeout={600}>
          <div>
            <Breadcrumb 
              items={[
                { label: 'Settings', path: '/settings' },
                { label: 'Prompt Templates', path: null }
              ]}
            />
            
            <div className="templates-header">
              <div>
                <Typography variant="h4" fontWeight="bold" color="text.primary">
                  Prompt Templates
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  Manage your AI prompt templates for different use cases
                </Typography>
              </div>
              
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<CategoryIcon />}
                  onClick={() => setTypeModalOpen(true)}
                  sx={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    '&:hover': { 
                      bgcolor: COLORS.blue[50],
                      borderColor: COLORS.blue[600]
                    },
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Manage Prompt Types
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleShowCreateForm}
                  sx={{
                    bgcolor: COLORS.primary,
                    '&:hover': { bgcolor: COLORS.blue[600] },
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Create Template
                </Button>
              </Box>
            </div>



            <div className="templates-content">
              {isCreating && (
                <CreateTemplateForm
                  onSave={handleCreateNew}
                  onCancel={handleCancelCreate}
                  promptTypes={promptTypes}
                />
              )}

              {(templates || MOCK_PROMPT_TEMPLATES).length > 0 ? (
                Object.entries(groupedTemplates).map(([type, typeTemplates]) => (
                  typeTemplates.length > 0 && (
                    <Accordion 
                      key={type} 
                      defaultExpanded 
                      sx={{ 
                        mb: 2, 
                        border: `1px solid ${COLORS.gray[200]}`,
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          bgcolor: COLORS.blue[50],
                          borderBottom: `1px solid ${COLORS.gray[200]}`,
                          '&:hover': { bgcolor: COLORS.blue[100] },
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center'
                          }
                        }}
                      >
                        <Typography variant="h6" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type}
                          <Box
                            sx={{
                              background: COLORS.primary,
                              color: 'white',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1
                            }}
                          >
                            {typeTemplates.length}
                          </Box>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ 
                        p: 2, 
                        bgcolor: COLORS.gray[25] || '#fafafa'
                      }}>
                        <div className="templates-list">
                          {typeTemplates.map((template) => (
                            <TemplateRow
                              key={template.id}
                              template={template}
                              onEdit={handleEdit}
                              onDelete={handleDeleteClick}
                              onSetActive={handleSetActive}
                            />
                          ))}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))
              ) : (
                !isCreating && (
                  <div className="empty-state">
                    <TemplateIcon sx={{ fontSize: 64, color: COLORS.gray[400], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" mb={1}>
                      No templates found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create your first prompt template to get started
                    </Typography>
                  </div>
                )
              )}
            </div>
          </div>
        </Fade>

        <TemplateModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          template={editingTemplate}
          promptTypes={promptTypes}
        />

        <CommonDeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Template"
          question="Are you sure you want to delete the template?"
          itemName={templateToDelete?.name || ''}
          warning="This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
        />

        <PromptTypeModal
          open={typeModalOpen}
          onClose={() => setTypeModalOpen(false)}
        />
      </main>
    </div>
  );
};

export default PromptTemplates;